// ── Ledger report helpers (Phase A3) ─────────────────────────────────────
// Pure aggregation over JournalEntry[] — no Firestore, no React. The report
// pages (Trial Balance, General Ledger, Balance Sheet, Profit & Loss) each
// load ledgerAccounts + journalEntries via onSnapshot and feed them here.
//
// Date handling matches the rest of the app: "YYYY-MM-DD" strings, half-open
// ranges [from, to) so a month range is start-of-month .. start-of-next-month
// with no double-count on the boundary.

import type {
  JournalEntry, LedgerAccount, LedgerAccountType, NormalBalance,
} from '../types';
import { SEED_COA } from './ledger';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface DateWindow { from?: string; to?: string }

export function inWindow(date: string, w?: DateWindow): boolean {
  if (!w) return true;
  if (w.from && date < w.from) return false;
  if (w.to && date >= w.to) return false;
  return true;
}

// ── Date-range presets (shared by the report pages + DateRangeFilter) ─────

export type DateFilterKey = 'this-month' | '3-months' | 'this-year' | 'all' | 'custom';

// Local YYYY-MM-DD: toISOString is UTC, which started "This month" on the
// last day of the previous month in time zones ahead of UTC.
const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const todayISO = () => toDateStr(new Date());

/** Half-open window [from, to). `null` only when a custom range is incomplete. */
export function computeDateWindow(
  key: DateFilterKey, customFrom: string, customTo: string,
): DateWindow | null {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (key) {
    case 'this-month': return { from: toDateStr(new Date(y, m, 1)),     to: toDateStr(new Date(y, m + 1, 1)) };
    case '3-months':   return { from: toDateStr(new Date(y, m - 2, 1)), to: toDateStr(new Date(y, m + 1, 1)) };
    case 'this-year':  return { from: toDateStr(new Date(y, 0, 1)),     to: toDateStr(new Date(y + 1, 0, 1)) };
    case 'all':        return {};
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const end = new Date(customTo + 'T00:00:00');
      end.setDate(end.getDate() + 1);          // inclusive To → half-open
      return { from: customFrom, to: toDateStr(end) };
    }
  }
}

export interface AccountMovement {
  accountId: string;
  debit:  number;   // Σ debits in the window
  credit: number;   // Σ credits in the window
  net:    number;    // debit − credit
  signed: number;   // net in the account's natural direction (+ = "more of what it normally holds")
}

/** Per-account totals across the given entries, optionally restricted to a date window. */
export function movements(
  entries: JournalEntry[],
  normalById: Record<string, NormalBalance>,
  w?: DateWindow,
): Map<string, AccountMovement> {
  const m = new Map<string, AccountMovement>();
  for (const e of entries) {
    if (!inWindow(e.date, w)) continue;
    for (const l of e.lines) {
      let cur = m.get(l.accountId);
      if (!cur) { cur = { accountId: l.accountId, debit: 0, credit: 0, net: 0, signed: 0 }; m.set(l.accountId, cur); }
      cur.debit  += l.debit  || 0;
      cur.credit += l.credit || 0;
    }
  }
  for (const cur of m.values()) {
    cur.debit  = round2(cur.debit);
    cur.credit = round2(cur.credit);
    cur.net    = round2(cur.debit - cur.credit);
    cur.signed = round2((normalById[cur.accountId] ?? 'debit') === 'debit' ? cur.net : -cur.net);
  }
  return m;
}

export function normalMap(accounts: LedgerAccount[]): Record<string, NormalBalance> {
  const m: Record<string, NormalBalance> = {};
  accounts.forEach(a => { m[a.id] = a.normalBalance; });
  return m;
}

/** Meta for an account id, falling back to a synthetic entry for a slug that
 *  has movement but no ledgerAccounts doc (e.g. a deleted bank account). */
export function accountMeta(
  accounts: LedgerAccount[],
): (id: string) => Pick<LedgerAccount, 'id' | 'code' | 'name' | 'type' | 'normalBalance' | 'sortOrder'> {
  const by = new Map(accounts.map(a => [a.id, a]));
  return (id: string) => {
    const a = by.get(id);
    if (a) return a;
    // A system account added to SEED_COA after this business last ran
    // "Seed / Repair" (e.g. sales-expenses) — use its seed definition so
    // it still lands on the right side of P&L / Balance Sheet.
    const seed = SEED_COA.find(s => s.slug === id);
    if (seed) return { id, code: seed.code, name: seed.name, type: seed.type, normalBalance: seed.normalBalance, sortOrder: seed.sortOrder };
    return { id, code: '', name: id, type: 'asset' as LedgerAccountType, normalBalance: 'debit' as NormalBalance, sortOrder: 9999 };
  };
}

// ── Trial Balance ────────────────────────────────────────────────────────

export interface TrialBalanceRow {
  accountId: string; code: string; name: string; type: LedgerAccountType;
  debit: number;   // shown in the Debit column (0 if this account nets credit)
  credit: number;  // shown in the Credit column
}
export interface TrialBalance {
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
}

export function trialBalance(
  entries: JournalEntry[], accounts: LedgerAccount[], asOf?: string,
): TrialBalance {
  const meta = accountMeta(accounts);
  const mv = movements(entries, normalMap(accounts), asOf ? { to: asOf } : undefined);
  const rows: TrialBalanceRow[] = [];
  let totalDebit = 0, totalCredit = 0;
  const ids = new Set<string>([...mv.keys(), ...accounts.map(a => a.id)]);
  for (const id of ids) {
    const net = mv.get(id)?.net ?? 0;
    if (Math.abs(net) < 0.005) continue;               // skip flat accounts
    const a = meta(id);
    const debit  = net > 0 ?  net : 0;
    const credit = net < 0 ? -net : 0;
    totalDebit  += debit;
    totalCredit += credit;
    rows.push({ accountId: id, code: a.code, name: a.name, type: a.type, debit, credit });
  }
  rows.sort((x, y) => (x.code || 'zzz').localeCompare(y.code || 'zzz'));
  totalDebit = round2(totalDebit); totalCredit = round2(totalCredit);
  return { rows, totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
}

// ── Profit & Loss (income − expense over a window) ────────────────────────

export interface PLLine { accountId: string; code: string; name: string; amount: number }
export interface PLStatement {
  income: PLLine[];      // signed positive = income earned
  expense: PLLine[];     // signed positive = expense incurred
  totalIncome: number;
  totalExpense: number;
  netProfit: number;
}

export function profitAndLoss(
  entries: JournalEntry[], accounts: LedgerAccount[], w: DateWindow,
): PLStatement {
  const meta = accountMeta(accounts);
  const mv = movements(entries, normalMap(accounts), w);
  const byType = new Map(accounts.map(a => [a.id, a.type]));
  const income: PLLine[] = [], expense: PLLine[] = [];
  for (const [id, m] of mv) {
    const t = byType.get(id) ?? meta(id).type;
    const a = meta(id);
    // Income earned = credits over debits (−net); a contra-income like
    // "Sales Returns" (normal debit) then shows negative. Expense incurred =
    // debits over credits (net); a contra-expense like "Inv Adj – Gain"
    // (normal credit) then shows negative.
    if (t === 'income'  && Math.abs(m.net) >= 0.005) income.push({ accountId: id, code: a.code, name: a.name, amount: round2(-m.net) });
    if (t === 'expense' && Math.abs(m.net) >= 0.005) expense.push({ accountId: id, code: a.code, name: a.name, amount: round2(m.net) });
  }
  const bycode = (x: PLLine, y: PLLine) => (x.code || 'zzz').localeCompare(y.code || 'zzz');
  income.sort(bycode); expense.sort(bycode);
  const totalIncome  = round2(income.reduce((s, l) => s + l.amount, 0));
  const totalExpense = round2(expense.reduce((s, l) => s + l.amount, 0));
  return { income, expense, totalIncome, totalExpense, netProfit: round2(totalIncome - totalExpense) };
}

// ── Balance Sheet (as of a date) ─────────────────────────────────────────

export interface BSLine { accountId: string; code: string; name: string; amount: number }
export interface BalanceSheet {
  assets: BSLine[];
  liabilities: BSLine[];
  equity: BSLine[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;         // includes currentEarnings
  currentEarnings: number;     // income − expense from inception to `asOf`, rolled into equity
  balanced: boolean;
}

export function balanceSheet(
  entries: JournalEntry[], accounts: LedgerAccount[], asOf?: string,
): BalanceSheet {
  const meta = accountMeta(accounts);
  const mv = movements(entries, normalMap(accounts), asOf ? { to: asOf } : undefined);
  const byType = new Map(accounts.map(a => [a.id, a.type]));

  const assets: BSLine[] = [], liabilities: BSLine[] = [], equity: BSLine[] = [];
  let incomeContribution = 0, expenseContribution = 0;
  for (const [id, m] of mv) {
    const t = byType.get(id) ?? meta(id).type;
    // Income/expense don't sit on the balance sheet — their net rolls into
    // equity as "current period earnings" (see below). Same sign convention
    // as profitAndLoss: income = −net, expense = net.
    if (t === 'income')  { incomeContribution  += -m.net; continue; }
    if (t === 'expense') { expenseContribution += m.net;  continue; }
    if (Math.abs(m.signed) < 0.005) continue;
    const a = meta(id);
    const line = { accountId: id, code: a.code, name: a.name, amount: m.signed };
    if (t === 'asset')     assets.push(line);
    if (t === 'liability') liabilities.push(line);
    if (t === 'equity')    equity.push(line);
  }
  const bycode = (x: BSLine, y: BSLine) => (x.code || 'zzz').localeCompare(y.code || 'zzz');
  assets.sort(bycode); liabilities.sort(bycode); equity.sort(bycode);

  const currentEarnings = round2(incomeContribution - expenseContribution);
  const totalAssets      = round2(assets.reduce((s, l) => s + l.amount, 0));
  const totalLiabilities = round2(liabilities.reduce((s, l) => s + l.amount, 0));
  const equitySansEarnings = round2(equity.reduce((s, l) => s + l.amount, 0));
  const totalEquity = round2(equitySansEarnings + currentEarnings);

  return {
    assets, liabilities, equity,
    totalAssets, totalLiabilities, totalEquity, currentEarnings,
    balanced: Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01,
  };
}

// ── General Ledger (one account, chronological, running balance) ──────────

export interface GLRow {
  entryId: string;
  date: string;
  sourceType: string;
  note?: string;
  debit: number;
  credit: number;
  balance: number;   // running, in the account's natural direction
}

export function generalLedger(
  entries: JournalEntry[], accountId: string, normal: NormalBalance, w?: DateWindow,
): { rows: GLRow[]; opening: number; closing: number } {
  // Opening balance = signed movement strictly before the window.
  let opening = 0;
  if (w?.from) {
    for (const e of entries) {
      if (e.date >= w.from) continue;
      for (const l of e.lines) {
        if (l.accountId !== accountId) continue;
        const net = (l.debit || 0) - (l.credit || 0);
        opening += normal === 'debit' ? net : -net;
      }
    }
  }
  opening = round2(opening);

  const flat: Omit<GLRow, 'balance'>[] = [];
  for (const e of entries) {
    if (!inWindow(e.date, w)) continue;
    for (const l of e.lines) {
      if (l.accountId !== accountId) continue;
      flat.push({
        entryId: e.id, date: e.date, sourceType: e.sourceType, note: e.note ?? l.memo,
        debit: l.debit || 0, credit: l.credit || 0,
      });
    }
  }
  flat.sort((a, b) => a.date.localeCompare(b.date) || a.entryId.localeCompare(b.entryId));

  let bal = opening;
  const rows: GLRow[] = flat.map(r => {
    const net = r.debit - r.credit;
    bal = round2(bal + (normal === 'debit' ? net : -net));
    return { ...r, balance: bal };
  });
  return { rows, opening, closing: bal };
}

export const TYPE_LABEL: Record<LedgerAccountType, string> = {
  asset:     'Assets',
  liability: 'Liabilities',
  equity:    'Equity',
  income:    'Income',
  expense:   'Expenses',
};
